import { supabase } from '../lib/supabase';

/**
 * ProgressMonitorService
 *
 * Tracks student progress in real-time and calculates key metrics:
 * - Sprints completed vs total
 * - Minutes behind/ahead of schedule
 * - Risk level assessment
 * - Projected completion date
 *
 * Triggered:
 * - When sprint starts
 * - When sprint completes
 * - When task completes
 * - Every 15 minutes during active sprint (via frontend)
 * - On demand via API
 */
class ProgressMonitorService {
  /**
   * Get current progress snapshot for a student
   * @param {string} userId - Student ID
   * @returns {Promise<Object>} Current progress snapshot
   */
  async getCurrentProgress(userId) {
    try {
      console.log('[ProgressMonitorService] Getting current progress for user:', userId);

      // Get the schedule
      const { data: schedule, error: scheduleError } = await supabase
        .from('sprint_schedules')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (scheduleError) {
        if (scheduleError.code === 'PGRST116') {
          console.log('[ProgressMonitorService] No sprint schedule found for user. User needs to run seed script.');
          return null;
        }
        throw scheduleError;
      }

      if (!schedule) {
        console.log('[ProgressMonitorService] Schedule is null');
        return null;
      }

      console.log('[ProgressMonitorService] Found schedule:', schedule.id);

      // Get all sprints for this schedule
      const { data: sprints, error: sprintsError } = await supabase
        .from('sprints')
        .select('*, sprint_tasks(*)')
        .eq('schedule_id', schedule.id)
        .order('sprint_number', { ascending: true });

      if (sprintsError) throw sprintsError;

      console.log('[ProgressMonitorService] Found', sprints?.length || 0, 'sprints');

      // Calculate progress metrics
      const metrics = this.calculateProgress(schedule, sprints);
      console.log('[ProgressMonitorService] Calculated metrics:', {
        sprintsCompleted: metrics.sprintsCompleted,
        sprintsTotal: metrics.sprintsTotal,
        sprintsBehind: metrics.sprintsBehind,
        riskLevel: metrics.riskLevel
      });

      // Get current sprint
      const currentSprint = sprints.find(s => s.status === 'IN_PROGRESS') || null;

      // Get current task (first incomplete task in current sprint)
      let currentTask = null;
      if (currentSprint && currentSprint.sprint_tasks) {
        currentTask = currentSprint.sprint_tasks.find(t => t.status !== 'COMPLETE' && t.status !== 'SKIPPED');
      }

      // Build snapshot
      const snapshot = {
        user_id: userId,
        schedule_id: schedule.id,
        timestamp: new Date().toISOString(),
        current_sprint_id: currentSprint?.id || null,
        current_task_id: currentTask?.id || null,
        sprints_completed: metrics.sprintsCompleted,
        sprints_total: metrics.sprintsTotal,
        tasks_completed: metrics.tasksCompleted,
        tasks_total: metrics.tasksTotal,
        time_spent: metrics.timeSpent,
        time_remaining: metrics.timeRemaining,
        on_schedule: metrics.onSchedule,
        minutes_behind: metrics.minutesBehind,
        sprints_behind: metrics.sprintsBehind,
        projected_completion_date: metrics.projectedCompletionDate,
        risk_level: metrics.riskLevel,
        average_task_quality: metrics.averageTaskQuality,
        burnout_risk: metrics.burnoutRisk
      };

      return snapshot;
    } catch (error) {
      console.error('[ProgressMonitorService] Error getting current progress:', error);
      throw error;
    }
  }

  /**
   * Calculate progress metrics from schedule and sprints
   * @param {Object} schedule - Sprint schedule
   * @param {Array} sprints - Array of sprints with tasks
   * @returns {Object} Progress metrics
   */
  calculateProgress(schedule, sprints) {
    const now = new Date();
    const scheduleStart = new Date(schedule.start_date);
    const scheduleEnd = new Date(schedule.end_date);

    // Filter out skipped sprints
    const activeSprints = sprints.filter(s => s.status !== 'SKIPPED');

    // Count completed sprints
    const completedSprints = activeSprints.filter(s => s.status === 'COMPLETE');
    const sprintsCompleted = completedSprints.length;
    const sprintsTotal = activeSprints.length;

    // Count tasks
    let tasksCompleted = 0;
    let tasksTotal = 0;
    activeSprints.forEach(sprint => {
      if (sprint.sprint_tasks) {
        const activeTasks = sprint.sprint_tasks.filter(t => !t.is_optional || t.status === 'COMPLETE');
        tasksTotal += activeTasks.length;
        tasksCompleted += activeTasks.filter(t => t.status === 'COMPLETE').length;
      }
    });

    // Calculate time spent (sum of actual durations)
    let timeSpent = 0;
    completedSprints.forEach(sprint => {
      if (sprint.actual_start_time && sprint.actual_end_time) {
        const start = new Date(sprint.actual_start_time);
        const end = new Date(sprint.actual_end_time);
        timeSpent += Math.floor((end - start) / 1000 / 60); // minutes
      }
    });

    // Calculate time remaining (sum of estimated durations for incomplete sprints)
    let timeRemaining = 0;
    activeSprints
      .filter(s => s.status !== 'COMPLETE')
      .forEach(sprint => {
        timeRemaining += sprint.estimated_duration;
      });

    // Calculate expected sprint index (which sprint should be done by now)
    const expectedSprintIndex = this.calculateExpectedSprintIndex(schedule, activeSprints, now);

    // Calculate sprints behind
    const sprintsBehind = Math.max(0, expectedSprintIndex - sprintsCompleted);

    // Calculate minutes behind
    const minutesBehind = this.calculateMinutesBehind(activeSprints, expectedSprintIndex, sprintsCompleted);

    // Determine if on schedule
    const onSchedule = sprintsBehind === 0;

    // Assess risk level
    const riskLevel = this.assessRiskLevel(sprintsBehind, schedule, now);

    // Project completion date
    const projectedCompletionDate = this.projectCompletionDate(
      schedule,
      sprintsCompleted,
      sprintsTotal,
      timeSpent,
      timeRemaining
    );

    // Calculate average task quality (from session metrics)
    const averageTaskQuality = null; // TODO: Calculate from session_metrics

    // Calculate burnout risk (from session metrics - energy levels)
    const burnoutRisk = null; // TODO: Calculate from session_metrics

    return {
      sprintsCompleted,
      sprintsTotal,
      tasksCompleted,
      tasksTotal,
      timeSpent,
      timeRemaining,
      onSchedule,
      minutesBehind,
      sprintsBehind,
      projectedCompletionDate,
      riskLevel,
      averageTaskQuality,
      burnoutRisk
    };
  }

  /**
   * Calculate which sprint should be completed by now
   * @param {Object} schedule - Sprint schedule
   * @param {Array} sprints - Array of sprints
   * @param {Date} now - Current time
   * @returns {number} Expected number of completed sprints
   */
  calculateExpectedSprintIndex(schedule, sprints, now) {
    const scheduleStart = new Date(schedule.start_date);
    const currentDay = Math.floor((now - scheduleStart) / (1000 * 60 * 60 * 24)) + 1;

    // Count how many sprints should be done by this day
    let expectedSprints = 0;
    for (const sprint of sprints) {
      if (sprint.day_number < currentDay) {
        expectedSprints++;
      } else if (sprint.day_number === currentDay) {
        // For today, check if we're past the suggested start time
        const suggestedTime = sprint.suggested_start_time;
        if (suggestedTime) {
          const [hours, minutes] = suggestedTime.split(':').map(Number);
          const suggestedDateTime = new Date(now);
          suggestedDateTime.setHours(hours, minutes, 0, 0);

          // Add estimated duration to get expected end time
          const expectedEnd = new Date(suggestedDateTime.getTime() + sprint.estimated_duration * 60000);

          if (now > expectedEnd) {
            expectedSprints++;
          }
        }
      }
    }

    return expectedSprints;
  }

  /**
   * Calculate minutes behind schedule
   * @param {Array} sprints - Array of sprints
   * @param {number} expectedIndex - Expected number of completed sprints
   * @param {number} actualCompleted - Actual number of completed sprints
   * @returns {number} Minutes behind
   */
  calculateMinutesBehind(sprints, expectedIndex, actualCompleted) {
    if (actualCompleted >= expectedIndex) {
      return 0;
    }

    // Sum estimated durations of sprints that should be done but aren't
    let minutesBehind = 0;
    for (let i = actualCompleted; i < expectedIndex; i++) {
      if (sprints[i]) {
        minutesBehind += sprints[i].estimated_duration;
      }
    }

    return minutesBehind;
  }

  /**
   * Assess risk level based on sprints behind and time remaining
   * @param {number} sprintsBehind - Number of sprints behind schedule
   * @param {Object} schedule - Sprint schedule
   * @param {Date} now - Current time
   * @returns {string} Risk level: ON_TRACK, AT_RISK, CRITICAL, EMERGENCY
   */
  assessRiskLevel(sprintsBehind, schedule, now) {
    const endDate = new Date(schedule.end_date);
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    // EMERGENCY: Deadline within 24 hours and behind
    if (daysRemaining <= 1 && sprintsBehind > 0) {
      return 'EMERGENCY';
    }

    // CRITICAL: 2+ sprints behind
    if (sprintsBehind >= 2) {
      return 'CRITICAL';
    }

    // AT_RISK: 1 sprint behind
    if (sprintsBehind === 1) {
      return 'AT_RISK';
    }

    // ON_TRACK: On pace or ahead
    return 'ON_TRACK';
  }

  /**
   * Project completion date based on current pace
   * @param {Object} schedule - Sprint schedule
   * @param {number} sprintsCompleted - Sprints completed
   * @param {number} sprintsTotal - Total sprints
   * @param {number} timeSpent - Minutes spent so far
   * @param {number} timeRemaining - Estimated minutes remaining
   * @returns {string|null} Projected completion date (ISO format)
   */
  projectCompletionDate(schedule, sprintsCompleted, sprintsTotal, timeSpent, timeRemaining) {
    if (sprintsCompleted === 0) {
      return schedule.end_date; // Not enough data, use planned end date
    }

    // Calculate average time per sprint
    const avgTimePerSprint = timeSpent / sprintsCompleted;

    // Calculate remaining sprints
    const remainingSprints = sprintsTotal - sprintsCompleted;

    // Project remaining time
    const projectedRemainingTime = remainingSprints * avgTimePerSprint;

    // Assume 5 hours of work per day
    const workHoursPerDay = 5;
    const remainingDays = Math.ceil(projectedRemainingTime / (workHoursPerDay * 60));

    // Calculate projected end date
    const now = new Date();
    const projectedEnd = new Date(now.getTime() + remainingDays * 24 * 60 * 60 * 1000);

    return projectedEnd.toISOString().split('T')[0];
  }

  /**
   * Save a progress snapshot to the database
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Object>} Saved snapshot
   */
  async saveSnapshot(snapshot) {
    try {
      const { data, error } = await supabase
        .from('progress_snapshots')
        .insert(snapshot)
        .select()
        .single();

      if (error) throw error;

      console.log('[ProgressMonitorService] Snapshot saved:', data.id);
      return data;
    } catch (error) {
      console.error('[ProgressMonitorService] Error saving snapshot:', error);
      throw error;
    }
  }

  /**
   * Get progress history for a student
   * @param {string} userId - Student ID
   * @param {number} days - Number of days to look back (default 7)
   * @returns {Promise<Array>} Array of progress snapshots
   */
  async getProgressHistory(userId, days = 7) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      const { data, error } = await supabase
        .from('progress_snapshots')
        .select('*')
        .eq('user_id', userId)
        .gte('timestamp', cutoffDate.toISOString())
        .order('timestamp', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[ProgressMonitorService] Error getting progress history:', error);
      throw error;
    }
  }

  /**
   * Create a progress snapshot on demand
   * @param {string} userId - Student ID
   * @returns {Promise<Object>} Created snapshot
   */
  async createSnapshot(userId) {
    const snapshot = await this.getCurrentProgress(userId);
    if (snapshot) {
      return await this.saveSnapshot(snapshot);
    }
    return null;
  }
}

export default new ProgressMonitorService();
